import React, { useState, useEffect } from 'react';
import merchantKycService from '../services/merchantKycService';
import KYCCompletionDebugger from '../utils/kycCompletionDebugger';
import './MerchantKycStatus.css';

const MerchantKycStatus = ({ merchantProfile, onProfileUpdate, onTabChange }) => {
  const [kycSummary, setKycSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadKycSummary();
  }, [merchantProfile]);

  // Add effect to listen for profile updates
  useEffect(() => {
    const handleProfileUpdate = (event) => {
      console.log('🔄 MerchantKycStatus: Profile update detected, refreshing status');
      loadKycSummary();
    };

    window.addEventListener('kycProfileUpdated', handleProfileUpdate);
    return () => {
      window.removeEventListener('kycProfileUpdated', handleProfileUpdate);
    };
  }, []);

  // Add effect to listen for verification completion
  useEffect(() => {
    const handleVerificationUpdate = () => {
      console.log('🔄 MerchantKycStatus: Verification update detected, refreshing status');
      loadKycSummary();
    };

    window.addEventListener('kycVerificationCompleted', handleVerificationUpdate);
    window.addEventListener('kycStatusUpdated', handleVerificationUpdate);

    return () => {
      window.removeEventListener('kycVerificationCompleted', handleVerificationUpdate);
      window.removeEventListener('kycStatusUpdated', handleVerificationUpdate);
    };
  }, []);

  const loadKycSummary = () => {
    try {
      // First, validate and sync verification status
      merchantKycService.validateAndSyncVerificationStatus(merchantProfile.merchantId);
      
      const summary = merchantKycService.getMerchantKycSummary(merchantProfile.merchantId);
      const completionPercentage = merchantKycService.calculateCompletionPercentage(merchantProfile.merchantId);
      
      console.log('🔍 MerchantKycStatus: Loading KYC summary', {
        merchantId: merchantProfile.merchantId,
        summary,
        calculatedPercentage: completionPercentage,
        profileStatus: merchantProfile.kycStatus
      });
      
      setKycSummary({
        ...summary,
        completionPercentage
      });
      
      // Update profile with latest completion percentage and status
      const verification = merchantKycService.getMerchantVerification(merchantProfile.merchantId);
      let statusNeedsUpdate = false;
      let statusUpdate = {};
      
      if (completionPercentage !== merchantProfile.kycStatus.completionPercentage) {
        console.log(`🔄 Updating completion percentage from ${merchantProfile.kycStatus.completionPercentage}% to ${completionPercentage}%`);
        statusUpdate.completionPercentage = completionPercentage;
        statusNeedsUpdate = true;
      }
      
      // If verification exists but profile status doesn't match, update it
      if (verification && verification.results) {
        const verificationStatus = verification.results.status;
        if (verificationStatus !== merchantProfile.kycStatus.status) {
          console.log(`🔄 Updating status from ${merchantProfile.kycStatus.status} to ${verificationStatus}`);
          statusUpdate.status = verificationStatus;
          statusUpdate.submittedAt = verification.timestamp;
          
          if (verificationStatus === 'APPROVED') {
            statusUpdate.approvedAt = verification.timestamp;
          } else if (verificationStatus === 'REJECTED') {
            statusUpdate.rejectedAt = verification.timestamp;
          }
          
          statusNeedsUpdate = true;
        }
      }
      
      if (statusNeedsUpdate) {
        const updatedProfile = merchantKycService.updateMerchantKycStatus(
          merchantProfile.merchantId,
          statusUpdate
        );
        if (updatedProfile && onProfileUpdate) {
          onProfileUpdate({
            ...merchantProfile,
            kycStatus: updatedProfile
          });
        }
      }
    } catch (error) {
      console.error('Error loading KYC summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      'NOT_INITIATED': {
        color: '#6c757d',
        icon: '⏸️',
        title: 'KYC Not Started',
        description: 'Begin your KYC verification to start accepting payments',
        nextStep: 'Complete your profile information to get started'
      },
      'IN_PROGRESS': {
        color: '#ffc107',
        icon: '⏳',
        title: 'KYC In Progress',
        description: 'Your KYC verification is currently being processed',
        nextStep: 'Continue uploading required documents'
      },
      'PENDING_REVIEW': {
        color: '#17a2b8',
        icon: '👀',
        title: 'Under Review',
        description: 'Our team is reviewing your submitted documents',
        nextStep: 'Please wait while we verify your information'
      },
      'APPROVED': {
        color: '#28a745',
        icon: '✅',
        title: 'KYC Approved',
        description: 'Congratulations! Your account is fully verified',
        nextStep: 'You can now start accepting payments'
      },
      'REJECTED': {
        color: '#dc3545',
        icon: '❌',
        title: 'KYC Rejected',
        description: 'Your KYC application needs attention',
        nextStep: 'Please review the feedback and resubmit'
      },
      'RESUBMISSION_REQUIRED': {
        color: '#fd7e14',
        icon: '🔄',
        title: 'Resubmission Required',
        description: 'Some documents need to be updated',
        nextStep: 'Update the required documents and resubmit'
      }
    };

    return statusMap[status] || statusMap['NOT_INITIATED'];
  };

  const getNextAction = (status, completionPercentage) => {
    if (status === 'APPROVED') {
      return {
        text: 'Go to Dashboard',
        action: () => window.location.href = '/dashboard',
        className: 'btn-success'
      };
    }

    if (status === 'REJECTED' || status === 'RESUBMISSION_REQUIRED') {
      return {
        text: 'Update Documents',
        action: () => onTabChange('documents'),
        className: 'btn-warning'
      };
    }

    if (completionPercentage < 30) {
      return {
        text: 'Complete Profile',
        action: () => onTabChange('profile'),
        className: 'btn-primary'
      };
    }

    if (completionPercentage < 70) {
      return {
        text: 'Upload Documents',
        action: () => onTabChange('documents'),
        className: 'btn-primary'
      };
    }

    return {
      text: 'Start Verification',
      action: () => onTabChange('verification'),
      className: 'btn-primary'
    };
  };

  const resetKycData = () => {
    if (window.confirm('Are you sure you want to reset all KYC data? This action cannot be undone.')) {
      merchantKycService.deleteMerchantKycData(merchantProfile.merchantId);
      
      // Reinitialize profile
      const newProfile = merchantKycService.initializeMerchantProfile(
        merchantProfile.merchantId,
        {
          fullName: merchantProfile.personalInfo.fullName,
          email: merchantProfile.personalInfo.email,
          businessName: merchantProfile.businessInfo.businessName
        }
      );
      
      onProfileUpdate(newProfile);
      loadKycSummary();
      
      if (window.showNotification) {
        window.showNotification('KYC data has been reset', 'info');
      }
    }
  };

  if (loading) {
    return (
      <div className="merchant-kyc-status">
        <div className="loading-spinner">Loading KYC status...</div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(merchantProfile.kycStatus.status);
  const nextAction = getNextAction(merchantProfile.kycStatus.status, kycSummary?.completionPercentage || 0);

  return (
    <div className="merchant-kyc-status">
      {/* Status Overview */}
      <div className="status-overview">
        <div className="status-header">
          <div className="status-icon" style={{ color: statusInfo.color }}>
            {statusInfo.icon}
          </div>
          <div className="status-info">
            <h2 style={{ color: statusInfo.color }}>{statusInfo.title}</h2>
            <p>{statusInfo.description}</p>
          </div>
        </div>

        <div className="completion-progress">
          <div className="progress-header">
            <span>Overall Progress</span>
            <span className="progress-percentage">{kycSummary?.completionPercentage || 0}%</span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ 
                width: `${kycSummary?.completionPercentage || 0}%`,
                backgroundColor: statusInfo.color 
              }}
            ></div>
          </div>
          <p className="next-step">{statusInfo.nextStep}</p>
        </div>
      </div>

      {/* KYC Checklist */}
      <div className="kyc-checklist">
        <h3>📋 KYC Verification Checklist</h3>
        <div className="checklist-grid">
          <div className="checklist-section">
            <h4>👤 Personal Information</h4>
            <div className="checklist-items">
              <div className={`checklist-item ${merchantProfile.personalInfo.fullName ? 'completed' : ''}`}>
                <span className="check-icon">
                  {merchantProfile.personalInfo.fullName ? '✅' : '⭕'}
                </span>
                <span>Full Name</span>
              </div>
              <div className={`checklist-item ${merchantProfile.personalInfo.email ? 'completed' : ''}`}>
                <span className="check-icon">
                  {merchantProfile.personalInfo.email ? '✅' : '⭕'}
                </span>
                <span>Email Address</span>
              </div>
              <div className={`checklist-item ${merchantProfile.personalInfo.phone ? 'completed' : ''}`}>
                <span className="check-icon">
                  {merchantProfile.personalInfo.phone ? '✅' : '⭕'}
                </span>
                <span>Phone Number</span>
              </div>
            </div>
          </div>

          <div className="checklist-section">
            <h4>🏢 Business Information</h4>
            <div className="checklist-items">
              <div className={`checklist-item ${merchantProfile.businessInfo.businessName ? 'completed' : ''}`}>
                <span className="check-icon">
                  {merchantProfile.businessInfo.businessName ? '✅' : '⭕'}
                </span>
                <span>Business Name</span>
              </div>
              <div className={`checklist-item ${merchantProfile.businessInfo.pan ? 'completed' : ''}`}>
                <span className="check-icon">
                  {merchantProfile.businessInfo.pan ? '✅' : '⭕'}
                </span>
                <span>PAN Number</span>
              </div>
              <div className={`checklist-item ${merchantProfile.businessInfo.registeredAddress ? 'completed' : ''}`}>
                <span className="check-icon">
                  {merchantProfile.businessInfo.registeredAddress ? '✅' : '⭕'}
                </span>
                <span>Business Address</span>
              </div>
            </div>
          </div>

          <div className="checklist-section">
            <h4>🏦 Bank Information</h4>
            <div className="checklist-items">
              <div className={`checklist-item ${merchantProfile.bankInfo.accountNumber ? 'completed' : ''}`}>
                <span className="check-icon">
                  {merchantProfile.bankInfo.accountNumber ? '✅' : '⭕'}
                </span>
                <span>Account Number</span>
              </div>
              <div className={`checklist-item ${merchantProfile.bankInfo.ifscCode ? 'completed' : ''}`}>
                <span className="check-icon">
                  {merchantProfile.bankInfo.ifscCode ? '✅' : '⭕'}
                </span>
                <span>IFSC Code</span>
              </div>
            </div>
          </div>

          <div className="checklist-section">
            <h4>📄 Documents</h4>
            <div className="checklist-items">
              <div className={`checklist-item ${kycSummary?.documentsCount > 0 ? 'completed' : ''}`}>
                <span className="check-icon">
                  {kycSummary?.documentsCount > 0 ? '✅' : '⭕'}
                </span>
                <span>Documents Uploaded ({kycSummary?.documentsCount || 0})</span>
              </div>
              <div className={`checklist-item ${kycSummary?.hasVerificationResults ? 'completed' : ''}`}>
                <span className="check-icon">
                  {kycSummary?.hasVerificationResults ? '✅' : '⭕'}
                </span>
                <span>Verification Complete</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-section">
        <button 
          className={`btn btn-lg ${nextAction.className}`}
          onClick={nextAction.action}
        >
          {nextAction.text}
        </button>

        {merchantProfile.kycStatus.status === 'APPROVED' && (
          <div className="success-message">
            <h4>🎉 Congratulations!</h4>
            <p>Your merchant account is fully verified and ready for business.</p>
            <ul>
              <li>✅ Accept online payments</li>
              <li>✅ Access merchant dashboard</li>
              <li>✅ View transaction analytics</li>
              <li>✅ Manage customer payments</li>
            </ul>
          </div>
        )}

        {(merchantProfile.kycStatus.status === 'REJECTED' || 
          merchantProfile.kycStatus.status === 'RESUBMISSION_REQUIRED') && 
          merchantProfile.kycStatus.rejectionReason && (
          <div className="rejection-feedback">
            <h4>📝 Feedback</h4>
            <p>{merchantProfile.kycStatus.rejectionReason}</p>
          </div>
        )}

        {/* Demo Reset Button */}
        <div className="demo-actions">
          <button 
            className="btn btn-outline btn-sm"
            onClick={resetKycData}
            style={{ marginRight: '10px' }}
          >
            🔄 Reset KYC Data (Demo)
          </button>
          
          <button 
            className="btn btn-outline btn-sm"
            onClick={() => {
              KYCCompletionDebugger.debugCompletion(merchantProfile.merchantId);
            }}
            style={{ marginRight: '10px' }}
          >
            🔍 Debug Completion
          </button>
          
          <button 
            className="btn btn-success btn-sm"
            onClick={() => {
              const newPercentage = KYCCompletionDebugger.fixCompletion(merchantProfile.merchantId);
              if (newPercentage !== null) {
                loadKycSummary(); // Refresh the display
                if (window.showNotification) {
                  window.showNotification(`Completion percentage fixed: ${newPercentage}%`, 'success');
                }
              }
            }}
            style={{ marginRight: '10px' }}
          >
            🔧 Fix Completion %
          </button>
          
          <button 
            className="btn btn-success btn-sm"
            onClick={() => {
              // Force 100% completion if verification is done
              const verification = merchantKycService.getMerchantVerification(merchantProfile.merchantId);
              if (verification && verification.results) {
                merchantKycService.updateMerchantKycStatus(merchantProfile.merchantId, {
                  completionPercentage: 100,
                  status: verification.results.status || 'APPROVED'
                });
                
                // Refresh the display
                loadKycSummary();
                
                if (window.showNotification) {
                  window.showNotification('Completion percentage set to 100%!', 'success');
                }
              } else {
                if (window.showNotification) {
                  window.showNotification('No verification results found. Complete verification first.', 'warning');
                }
              }
            }}
            style={{ marginRight: '10px' }}
          >
            🎯 Force 100%
          </button>
          
          <button 
            className="btn btn-warning btn-sm"
            onClick={() => {
              // Validate and fix merchant data integrity
              const validation = merchantKycService.validateMerchantDataIntegrity(merchantProfile.merchantId);
              
              console.group('🔍 Merchant Data Integrity Check');
              console.log('Merchant ID:', merchantProfile.merchantId);
              console.log('Validation Result:', validation);
              console.groupEnd();
              
              if (!validation.valid) {
                if (window.confirm(`Data integrity issues found:\n${validation.issues.join('\n')}\n\nWould you like to fix these issues? This will reset corrupted data.`)) {
                  const fixed = merchantKycService.fixMerchantDataIntegrity(merchantProfile.merchantId);
                  if (fixed) {
                    // Refresh the page to reload clean data
                    window.location.reload();
                  }
                }
              } else {
                if (window.showNotification) {
                  window.showNotification('Merchant data integrity is valid!', 'success');
                }
              }
            }}
            style={{ marginRight: '10px' }}
          >
            🔧 Fix Data Integrity
          </button>
          
          <button 
            className="btn btn-info btn-sm"
            onClick={() => {
              KYCCompletionDebugger.checkVerificationStatus(merchantProfile.merchantId);
              
              // Also check localStorage directly
              console.group('🔍 Direct Storage Check');
              const verificationKey = `merchant_verification_${merchantProfile.merchantId}`;
              const storedVerification = localStorage.getItem(verificationKey);
              console.log('Verification Key:', verificationKey);
              console.log('Stored Verification:', storedVerification);
              if (storedVerification) {
                try {
                  const parsed = JSON.parse(storedVerification);
                  console.log('Parsed Verification:', parsed);
                } catch (e) {
                  console.error('Failed to parse verification data:', e);
                }
              }
              console.groupEnd();
            }}
            style={{ marginRight: '10px' }}
          >
            🔍 Check Verification
          </button>
          
          <button 
            className="btn btn-warning btn-sm"
            onClick={() => {
              // Force refresh all data
              console.log('🔄 Force refreshing all KYC data...');
              loadKycSummary();
              
              // Trigger parent component refresh
              if (onProfileUpdate) {
                const refreshedProfile = merchantKycService.getMerchantProfile(merchantProfile.merchantId);
                if (refreshedProfile) {
                  onProfileUpdate(refreshedProfile);
                }
              }
              
              if (window.showNotification) {
                window.showNotification('KYC data refreshed from storage', 'info');
              }
            }}
          >
            🔄 Force Refresh
          </button>
        </div>
      </div>

      {/* Timeline */}
      {merchantProfile.kycStatus.submittedAt && (
        <div className="kyc-timeline">
          <h3>📅 KYC Timeline</h3>
          <div className="timeline-items">
            <div className="timeline-item completed">
              <div className="timeline-icon">📝</div>
              <div className="timeline-content">
                <h3>Application Submitted</h3>
                <p>{new Date(merchantProfile.kycStatus.submittedAt).toLocaleDateString()}</p>
              </div>
            </div>
            
            {merchantProfile.kycStatus.status === 'PENDING_REVIEW' && (
              <div className="timeline-item active">
                <div className="timeline-icon">👀</div>
                <div className="timeline-content">
                  <h3>Under Review</h3>
                  <p>Expected completion: 24-48 hours</p>
                </div>
              </div>
            )}
            
            {merchantProfile.kycStatus.approvedAt && (
              <div className="timeline-item completed">
                <div className="timeline-icon">✅</div>
                <div className="timeline-content">
                  <h3>Approved</h3>
                  <p>{new Date(merchantProfile.kycStatus.approvedAt).toLocaleDateString()}</p>
                </div>
              </div>
            )}
            
            {merchantProfile.kycStatus.rejectedAt && (
              <div className="timeline-item rejected">
                <div className="timeline-icon">❌</div>
                <div className="timeline-content">
                  <h3>Rejected</h3>
                  <p>{new Date(merchantProfile.kycStatus.rejectedAt).toLocaleDateString()}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MerchantKycStatus;